import { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Play, Share2, Heart, X } from 'lucide-react';

interface VideoInfo {
  titulo: string;
  descripcion: string;
  videoSrc: string;
  thumbnail: string;
  duracion: string;
  categoria: string;
}

const VIDEOS_EDUCATIVOS: VideoInfo[] = [
  {
    titulo: '¿Qué es una EPS?',
    descripcion:
      'Conoce qué son las Entidades Prestadoras de Salud (EPS), su rol en el sistema de salud peruano y los beneficios que ofrecen a sus afiliados.',
    videoSrc: '/que-es-eps.mp4',
    thumbnail: '/soluciones.png',
    duracion: '4:30',
    categoria: 'Conceptos Básicos',
  },
  {
    titulo: '¿Cómo Funciona las EPS?',
    descripcion:
      'Descubre el funcionamiento de las Entidades Prestadoras de Salud (EPS) en el Perú, sus servicios, cobertura y cómo brindan atención médica de calidad a sus afiliados.',
    videoSrc: '/como-funciona-eps.mp4',
    thumbnail: '/soluciones.png',
    duracion: '5:15',
    categoria: 'Sistema de Salud',
  },
  {
    titulo: '¿Cómo usar tu EPS?',
    descripcion:
      'Aprende paso a paso cómo utilizar los servicios de tu EPS, desde la afiliación hasta el acceso a consultas médicas, procedimientos y beneficios adicionales.',
    videoSrc: '/como-usar-eps.mp4',
    thumbnail: '/soluciones.png',
    duracion: '3:45',
    categoria: 'Guía Práctica',
  },
];

function VideoModal({
  video,
  onClose,
}: {
  video: VideoInfo;
  onClose: () => void;
}): React.ReactElement {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const handleLoadedData = useCallback(() => {
    setIsLoading(false);
    videoRef.current?.play().catch(() => {});
  }, []);

  return createPortal(
    <div className="fixed inset-0" style={{ zIndex: 2147483647, isolation: 'isolate' }}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="flex h-full w-full items-center justify-center p-4">
        <div className="relative z-10 max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl">
          <button
            onClick={onClose}
            className="border-border bg-muted hover:bg-muted/80 absolute top-4 right-4 z-50 flex h-10 w-10 items-center justify-center rounded-full border shadow-lg transition-all duration-300 hover:scale-110"
            title="Cerrar video"
          >
            <X className="text-muted-foreground h-5 w-5" />
          </button>

          <div className="flex flex-col">
            <div className="relative w-full bg-black" style={{ aspectRatio: '16/9' }}>
              <video
                ref={videoRef}
                src={video.videoSrc}
                className="h-full w-full rounded-t-2xl"
                controls
                preload="metadata"
                poster="/soluciones.png"
                onLoadedData={handleLoadedData}
                onError={() => setIsLoading(false)}
              >
                <track kind="captions" label="Español" srcLang="es" default />
              </video>

              {isLoading && (
                <div className="absolute inset-0 rounded-t-2xl bg-black/70">
                  <div className="flex h-full items-center justify-center">
                    <div className="text-center text-white">
                      <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-white" />
                      <p>Cargando video...</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6">
              <h3 className="text-foreground mb-3 text-xl font-bold md:text-2xl">{video.titulo}</h3>
              <p className="text-muted-foreground text-base leading-relaxed">{video.descripcion}</p>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function VideoCard({ video }: { video: VideoInfo }): React.ReactElement {
  const [modalOpen, setModalOpen] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);

  const handleShare = useCallback(() => {
    const shareData = {
      title: video.titulo,
      text: 'Mira este video educativo de APEPS',
      url: window.location.href,
    };

    if (navigator.share) {
      navigator.share(shareData).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  }, [video.titulo]);

  return (
    <>
      <div className="group relative overflow-hidden rounded-3xl border border-white/60 bg-white/90 shadow-xl backdrop-blur-xl transition-all duration-700 ease-out hover:-translate-y-2 hover:scale-105 hover:shadow-2xl">
        {/* Fondo con gradientes animados */}
        <div className="from-primary/5 via-card/30 to-secondary/5 absolute inset-0 bg-linear-to-br opacity-0 transition-all duration-700 group-hover:opacity-100" />
        <div className="absolute inset-0 bg-linear-to-tr from-transparent via-white/20 to-transparent opacity-0 transition-all duration-700 group-hover:opacity-100" />

        {/* Efecto de luz superior */}
        <div className="from-primary via-primary to-secondary absolute top-0 left-0 h-1 w-full origin-left scale-x-0 bg-linear-to-r opacity-0 transition-all duration-700 group-hover:scale-x-100 group-hover:opacity-100" />

        <div className="relative z-10">
          {/* Contenedor del video con thumbnail */}
          <div className="relative aspect-video overflow-hidden">
            <img
              src={video.thumbnail}
              alt={`Thumbnail de ${video.titulo}`}
              className="h-full w-full object-cover transition-all duration-500 group-hover:scale-110"
              loading="lazy"
            />

            {/* Overlay con degradado */}
            <div className="absolute inset-0 bg-linear-to-t from-black/70 via-black/20 to-transparent transition-all duration-500 group-hover:from-black/60" />

            {/* Badge de categoría */}
            <div className="bg-primary/90 absolute top-4 left-4 rounded-full px-4 py-2 text-sm font-semibold text-white shadow-lg backdrop-blur-sm">
              {video.categoria}
            </div>

            {/* Badge de duración */}
            <div className="absolute top-4 right-4 rounded-full bg-black/70 px-3 py-1 text-sm font-medium text-white backdrop-blur-sm">
              {video.duracion}
            </div>

            {/* Botón de play centralizado */}
            <div className="absolute inset-0 flex items-center justify-center">
              <button
                onClick={() => setModalOpen(true)}
                className="group/btn relative flex h-20 w-20 items-center justify-center rounded-full bg-white/90 shadow-2xl backdrop-blur-sm transition-all duration-500 hover:scale-110 hover:bg-white"
                aria-label={`Reproducir video: ${video.titulo}`}
              >
                <div className="border-primary/30 absolute inset-0 animate-pulse rounded-full border-4" />
                <div className="border-primary/50 absolute inset-0 animate-ping rounded-full border-2" />
                <div className="relative ml-1">
                  <Play className="text-primary group-hover/btn:text-primary h-8 w-8 transition-colors duration-300" />
                </div>
              </button>
            </div>

            {/* Efectos de partículas en hover */}
            <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-60">
              <div className="bg-primary/50 absolute top-1/4 left-1/4 h-2 w-2 animate-pulse rounded-full" />
              <div className="bg-primary/50 absolute top-3/4 right-1/3 h-1.5 w-1.5 animate-pulse rounded-full delay-300" />
              <div className="bg-secondary/50 absolute top-1/2 right-1/4 h-2.5 w-2.5 animate-pulse rounded-full delay-700" />
              <div className="bg-primary/40 absolute bottom-1/3 left-1/3 h-1 w-1 animate-pulse rounded-full delay-1000" />
            </div>
          </div>

          {/* Contenido del video */}
          <div className="p-6">
            <h3 className="text-foreground group-hover:text-primary mb-3 text-xl leading-tight font-bold transition-colors duration-300">
              {video.titulo}
            </h3>
            <p className="text-muted-foreground group-hover:text-foreground mb-4 leading-relaxed transition-colors duration-300">
              {video.descripcion}
            </p>

            {/* Separador decorativo */}
            <div className="from-primary to-primary/80 mb-4 h-0.5 w-12 origin-left scale-x-0 bg-linear-to-r transition-transform duration-500 group-hover:scale-x-100" />

            {/* Acciones */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => setModalOpen(true)}
                className="text-primary hover:text-primary/80 flex items-center space-x-2 font-medium transition-colors duration-300"
              >
                <Play className="h-4 w-4" />
                <span>Ver video</span>
              </button>

              <div className="flex items-center space-x-2">
                <button
                  onClick={handleShare}
                  className="bg-muted/60 hover:bg-primary/10 flex h-10 w-10 items-center justify-center rounded-full transition-all duration-300 hover:-translate-y-1 hover:scale-110"
                  title="Compartir video"
                >
                  <Share2 className="text-muted-foreground hover:text-primary h-4 w-4 transition-colors duration-300" />
                </button>
                <button
                  onClick={() => setIsFavorite(!isFavorite)}
                  className={`flex h-10 w-10 items-center justify-center rounded-full transition-all duration-300 hover:-translate-y-1 hover:scale-110 ${isFavorite ? 'bg-destructive/15' : 'bg-muted/60 hover:bg-destructive/15'}`}
                  title="Agregar a favoritos"
                >
                  <Heart
                    className={`h-4 w-4 transition-colors duration-300 ${isFavorite ? 'text-destructive' : 'text-muted-foreground hover:text-destructive'}`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Efecto de borde brillante en hover */}
        <div className="group-hover:border-primary/20 pointer-events-none absolute inset-0 rounded-3xl border-2 border-transparent transition-all duration-500" />
      </div>

      {modalOpen && <VideoModal video={video} onClose={() => setModalOpen(false)} />}
    </>
  );
}

export function VideosInformativos(): React.ReactElement {
  return (
    <section className="relative overflow-hidden py-16">
      <div className="from-primary/5 to-primary/5 absolute inset-0 bg-linear-to-br via-white" />

      <div className="relative z-10 container mx-auto px-4">
        <div className="mx-auto max-w-7xl">
          {/* Encabezado de videos */}
          <div className="mb-16 text-center">
            <div className="bg-primary/10 mb-6 inline-flex items-center space-x-2 rounded-full px-6 py-3 backdrop-blur-sm">
              <div className="bg-primary h-3 w-3 animate-pulse rounded-full" />
              <span className="text-primary font-semibold">Contenido Educativo</span>
            </div>
            <h2 className="text-foreground mb-6 text-4xl font-bold md:text-5xl">
              Videos{' '}
              <span className="from-primary to-secondary bg-linear-to-r bg-clip-text text-transparent">
                Informativos
              </span>
            </h2>
            <p className="text-muted-foreground mx-auto max-w-3xl text-xl leading-relaxed">
              Aprende sobre el sistema de salud peruano, seguros complementarios y centros de
              vacunación a través de nuestro contenido educativo audiovisual.
            </p>
          </div>

          {/* Grid de videos */}
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {VIDEOS_EDUCATIVOS.map(video => (
              <VideoCard key={video.titulo} video={video} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
